import { createContext, type ReactNode, useContext, useId, useMemo } from 'react';
import styles from './Field.module.css';
import Icon from './Icon';

interface FieldContextValue {
  controlId: string;
  describedBy?: string;
  invalid: boolean;
  required: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/**
 * The props a control should spread onto its input element.
 *
 * Controls call this instead of taking `id` / `aria-describedby` / `aria-invalid`
 * as props, which is what makes `<Field><TextInput/></Field>` correct by
 * construction: there is no way to render a labelled field whose label points
 * at nothing, because no call site is responsible for wiring the ids.
 *
 * Returns nothing when the control is used outside a Field. That is allowed
 * (a bare search box in a toolbar has no visible label) but it means the caller
 * owns the accessible name, via `aria-label`.
 */
export function useFieldControl(): {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  required?: boolean;
} {
  const ctx = useContext(FieldContext);
  if (!ctx) return {};
  return {
    id: ctx.controlId,
    'aria-describedby': ctx.describedBy,
    'aria-invalid': ctx.invalid || undefined,
    required: ctx.required || undefined,
  };
}

/** True when the surrounding Field is in an error state. Lets a control render
    its own invalid styling without the caller passing the flag twice. */
export function useFieldInvalid(): boolean {
  return useContext(FieldContext)?.invalid ?? false;
}

export interface FieldProps {
  children: ReactNode;
  label: string;
  /** Guidance shown under the control. Replaced by `error` when there is one,
      because two lines of small text under an input is one line too many. */
  help?: string;
  /** Set it to a message and the field turns invalid. Empty string and
      undefined both mean valid, so `error={errors.name}` reads naturally. */
  error?: string | null;
  required?: boolean;
  /** Marks the field "Optional" in the label. Use when most fields in the form
      are required, so the exceptions are the short list. */
  optional?: boolean;
  className?: string;
}

/**
 * Label, control, and the one line of help or error under it.
 *
 * Wrap exactly one control. If two inputs belong together (a date range, a
 * first and last name), give them a `<fieldset>` with a `<legend>` and put a
 * Field around each one inside it.
 */
export default function Field({
  children,
  label,
  help,
  error,
  required = false,
  optional = false,
  className,
}: FieldProps) {
  const uid = useId();
  const controlId = `${uid}-control`;
  const helpId = `${uid}-help`;
  const errorId = `${uid}-error`;
  const invalid = Boolean(error);

  const value = useMemo<FieldContextValue>(
    () => ({
      controlId,
      // Points at whichever message is actually rendered. Naming an element that
      // is not in the DOM makes the description silently vanish.
      describedBy: invalid ? errorId : help ? helpId : undefined,
      invalid,
      required,
    }),
    [controlId, errorId, helpId, invalid, help, required],
  );

  return (
    <FieldContext.Provider value={value}>
      <div className={[styles.field, className ?? ''].filter(Boolean).join(' ')}>
        <label className={styles.label} htmlFor={controlId}>
          {label}
          {required ? (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          ) : null}
          {optional ? <span className={styles.optional}>Optional</span> : null}
        </label>
        {children}
        {invalid ? (
          // `role="alert"` so the message is announced when it appears after a
          // failed submit, rather than only being found by someone who goes
          // looking for it.
          <p className={styles.error} id={errorId} role="alert">
            <Icon name="help" size={13} className={styles.errorIcon} />
            {error}
          </p>
        ) : help ? (
          <p className={styles.help} id={helpId}>
            {help}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
